import React, { FC, useState } from 'react'
import { Box, Typography, TextField, IconButton } from '@mui/material'
import { ContentCopy, CheckCircle } from '@mui/icons-material'
import useStyles from './totp-setup-styles'

interface TotpSetupProps {
  qrCode: string // base64 encoded QR code image
  secret: string
  issuer: string
}

const TotpSetup: FC<TotpSetupProps> = ({ qrCode, secret, issuer }) => {
  const { classes } = useStyles()
  const [copied, setCopied] = useState(false)

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Box className={classes.container}>
      <Typography variant="h6" className={classes.title}>
        Set Up Authenticator App
      </Typography>

      <Typography variant="body2" className={classes.instructions}>
        Scan the QR code below with your authenticator app (Google
        Authenticator, Authy, Microsoft Authenticator, etc.)
      </Typography>

      <Box className={classes.qrContainer}>
        <img
          src={qrCode}
          alt="QR Code for authenticator app"
          className={classes.qrImage}
        />
      </Box>

      <Typography variant="body2" className={classes.alternativeText}>
        Can't scan the code? Enter this key manually in your app:
      </Typography>

      <Box className={classes.secretContainer}>
        <TextField
          value={secret}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton
                size="small"
                onClick={handleCopySecret}
                className={classes.copyButton}
                aria-label="Copy secret key"
              >
                {copied ? (
                  <CheckCircle color="success" fontSize="small" />
                ) : (
                  <ContentCopy fontSize="small" />
                )}
              </IconButton>
            ),
          }}
          className={classes.secretField}
        />
      </Box>

      {copied && (
        <Typography
          variant="caption"
          color="success"
          className={classes.copiedText}
        >
          Secret key copied to clipboard!
        </Typography>
      )}

      <Box className={classes.stepsContainer}>
        <Typography variant="subtitle2" className={classes.stepsTitle}>
          Steps:
        </Typography>
        <ol className={classes.stepsList}>
          <li>
            <Typography variant="body2">
              Download an authenticator app if you don't have one
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Scan the QR code or enter the secret key manually
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Enter the 6-digit code from your app to verify
            </Typography>
          </li>
        </ol>
      </Box>
    </Box>
  )
}

export default TotpSetup
